import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { db, isAbortError } from '../../lib/supabase'
import { formatDate } from '../../lib/dateUtils'
import { ErrorAlert } from '../../components/ui'

function getPairStatus(pair, t) {
  if (pair.male_survey_completed_at && pair.female_survey_completed_at) return { label: t('facilitatorDashboard.pairStatuses.bothCompleted'), style: 'bg-green-100 text-green-700' }
  if (pair.male_survey_completed_at || pair.female_survey_completed_at) return { label: t('facilitatorDashboard.pairStatuses.partiallyCompleted'), style: 'bg-blue-100 text-blue-700' }
  if (pair.male_password_set_at || pair.female_password_set_at) return { label: t('facilitatorDashboard.pairStatuses.inProgress'), style: 'bg-indigo-100 text-indigo-700' }
  return { label: t('facilitatorDashboard.pairStatuses.invited'), style: 'bg-gray-100 text-gray-600' }
}

export default function FacilitatorDashboard() {
  const { t, i18n } = useTranslation('admin')
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'
  const [pairs, setPairs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadPairs = async () => {
      try {
        const data = await db.getClientPairs()
        setPairs(data)
      } catch (err) {
        if (isAbortError(err)) return
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    loadPairs()
  }, [])

  if (loading) {
    return <div className="text-center py-12 text-gray-500">{t('facilitatorDashboard.loadingPairs')}</div>
  }

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-medium text-gray-800">{t('facilitatorDashboard.title')}</h2>
          <p className="text-gray-500 text-sm mt-1">{t('facilitatorDashboard.pairCount', { count: pairs.length })}</p>
        </div>
        <Link
          to="/facilitator/create-pair"
          className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5"
        >
          {t('facilitatorDashboard.createNewPair')}
        </Link>
      </div>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      {pairs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="font-display text-lg font-medium text-gray-800 mb-2">{t('facilitatorDashboard.noPairsTitle')}</h3>
          <p className="text-gray-500 mb-4">
            {t('facilitatorDashboard.noPairsMessageBefore')}
            <Link to="/facilitator/manual" className="text-primary-600 hover:text-primary-700 underline font-medium">
              {t('facilitatorDashboard.noPairsGuideLink')}
            </Link>
            {t('facilitatorDashboard.noPairsMessageAfter')}
          </p>
          <Link
            to="/facilitator/create-pair"
            className="inline-block px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg"
          >
            {t('facilitatorDashboard.createClientPair')}
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDashboard.tableHeaders.maleClient')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDashboard.tableHeaders.femaleClient')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDashboard.tableHeaders.status')}</th>
                <th className="text-left px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDashboard.tableHeaders.created')}</th>
                <th className="text-right px-5 py-3 text-sm font-semibold text-gray-600">{t('facilitatorDashboard.tableHeaders.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pairs.map(pair => {
                const status = getPairStatus(pair, t)
                const bothComplete = pair.male_respondent_id && pair.female_respondent_id
                return (
                  <tr key={pair.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{pair.male_name}</div>
                      <div className="text-gray-500 text-sm">{pair.male_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">{pair.female_name}</div>
                      <div className="text-gray-500 text-sm">{pair.female_email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${status.style}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-gray-500 text-sm">
                      {formatDate(pair.created_at, locale)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/facilitator/pair/${pair.id}`}
                          className="px-3 py-1.5 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                        >
                          {t('facilitatorDashboard.details')}
                        </Link>
                        {bothComplete && pair.male_respondent_id && (
                          <Link
                            to={`/facilitator/couple-analysis/${pair.male_respondent_id}`}
                            className="px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            {t('facilitatorDashboard.analysis')}
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
